// Reusable Client → Project → Folder-tree document browser (Phase 3).
// Mounted in two places: the global /app/documents page (with client/project
// selectors) and the project Documents tab (projectId pre-bound). Native HTML5
// drag-drop for doc→folder move and folder→folder reparent (optimistic + rollback).
import { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    FolderPlus, Upload, Download, FileText, ChevronRight, Pencil, Trash2,
    Loader2, List, FolderTree as FolderTreeIcon, X, Check, Home, Send, Files, Eye,
    Megaphone, EyeOff, ShieldAlert, FilePlus2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import { Skeleton } from "@/components/ui/skeleton";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import FolderTree from "./FolderTree";
import PublishWizard from "@/components/issues/PublishWizard";
import CreateReportMenu from "./CreateReportMenu";
import { UploadDialog, BulkUploadDialog, DocumentPreviewDialog, DocumentDetail, STATUS_TONE, formatBytes, fmtDate } from "./DocumentDialogs";

const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

export default function DocumentBrowser({ clientId, projectId, projectLabel, user, clients = [] }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const isInternal = user && INTERNAL_ROLES.has(user.role);
    const showPortalInfo = isInternal || user?.role === "client_admin";
    const canEdit = user && new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]).has(user.role);
    const canApprove = isInternal;
    const canManageFolders = isInternal; // create/rename/delete/move = internal only
    const canCreateReport = user && user.role !== "reviewer";
    const canCreateFilingRule = user && new Set(["director", "administrator"]).has(user.role);

    const [view, setView] = useState("folder");          // folder | flat
    const [selectedId, setSelectedId] = useState("__all__");
    const [uploadOpen, setUploadOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkPrefill, setBulkPrefill] = useState(null);
    const [previewDoc, setPreviewDoc] = useState(null);
    const [uploadPrefill, setUploadPrefill] = useState(null);
    const [detailDoc, setDetailDoc] = useState(null);
    const [newFolder, setNewFolder] = useState(null);    // {parent_id}
    const [renameTarget, setRenameTarget] = useState(null);
    const [draftName, setDraftName] = useState("");
    const [issueFor, setIssueFor] = useState(null);      // folder → open the publish wizard targeted at it
    const [selectedDocIds, setSelectedDocIds] = useState([]);

    const enabled = !!(clientId && projectId);

    const foldersQ = useQuery({
        queryKey: ["folders", clientId, projectId],
        queryFn: () => api.get("/folders", { params: { client_id: clientId, project_id: projectId, scope: "project" } }).then((r) => r.data),
        enabled,
    });
    const docsQ = useQuery({
        queryKey: ["project-docs", clientId, projectId],
        queryFn: () => api.get("/documents", { params: { project_id: projectId } }).then((r) => r.data),
        enabled,
    });
    const metaQ = useQuery({ queryKey: ["doc-meta"], queryFn: () => api.get("/documents/meta/categories").then((r) => r.data) });
    const buildingsQ = useQuery({ queryKey: ["buildings", clientId], queryFn: () => api.get("/buildings", { params: { client_id: clientId } }).then((r) => r.data), enabled: !!clientId });
    const allocationsQ = useQuery({
        queryKey: ["template-allocations", projectId],
        queryFn: () => api.get("/template-allocations", { params: { project_id: projectId } }).then((r) => r.data),
        enabled,
    });
    const reportsQ = useQuery({
        queryKey: ["allocation-reports", projectId],
        queryFn: () => api.get("/forms/instances", { params: { project_id: projectId } }).then((r) => r.data),
        enabled,
    });

    const folders = foldersQ.data || [];
    const docs = docsQ.data || [];
    const allocations = allocationsQ.data || [];
    const genReports = (reportsQ.data || []).filter((r) => r.allocation_id);
    const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);
    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const buildingMap = useMemo(() => Object.fromEntries((buildingsQ.data || []).map((b) => [b.id, b])), [buildingsQ.data]);

    const docCounts = useMemo(() => {
        const m = {};
        docs.forEach((d) => { if (d.folder_id) m[d.folder_id] = (m[d.folder_id] || 0) + 1; });
        return m;
    }, [docs]);
    const unfiledCount = docs.filter((d) => !d.folder_id).length;

    const visibleDocs = useMemo(() => {
        if (view === "flat" || selectedId === "__all__") return docs;
        if (selectedId === "__unfiled__") return docs.filter((d) => !d.folder_id);
        return docs.filter((d) => d.folder_id === selectedId);
    }, [docs, selectedId, view]);

    const breadcrumb = useMemo(() => {
        if (selectedId === "__all__" || selectedId === "__unfiled__") return [];
        const path = [];
        let cur = folderById[selectedId];
        let guard = 0;
        while (cur && guard < 8) { path.unshift(cur); cur = cur.parent_id ? folderById[cur.parent_id] : null; guard++; }
        return path;
    }, [selectedId, folderById]);

    const invalidate = useCallback(() => {
        qc.invalidateQueries({ queryKey: ["folders", clientId, projectId] });
        qc.invalidateQueries({ queryKey: ["project-docs", clientId, projectId] });
        qc.invalidateQueries({ queryKey: ["template-allocations", projectId] });
        qc.invalidateQueries({ queryKey: ["allocation-reports", projectId] });
    }, [qc, clientId, projectId]);

    // Allocated report templates the current user may generate a report from.
    // PR-completed templates are completed by PR on a visit — portal users can view but not self-generate.
    const canGenerateAlloc = (a) => user && user.role !== "reviewer" && (isInternal || a.completed_by === "client");
    const generatableAllocs = useMemo(() => allocations.filter(canGenerateAlloc), [allocations, isInternal, user]);

    async function generateReport(alloc) {
        try {
            const { data } = await api.post(`/template-allocations/${alloc.id}/generate`, { project_id: projectId });
            toast.success("Report created — complete it below");
            invalidate();
            navigate(`/app/inspections/${data.instance_id}?back=${encodeURIComponent(`/app/projects/${projectId}`)}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create report");
        }
    }
    async function deleteAllocation(a) {
        if (!window.confirm(`Remove the template "${a.title || a.template_name}" from this client? Existing reports are kept.`)) return;
        try {
            await api.delete(`/template-allocations/${a.id}`);
            toast.success("Template removed");
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not remove");
        }
    }

    // ---- drag-drop handlers ----
    const onDropDoc = useCallback(async (targetFolderId, docIds) => {
        try {
            await api.post("/documents/move", { document_ids: docIds, folder_id: targetFolderId });
            const targetFolder = targetFolderId ? folderById[targetFolderId] : null;
            const anyVisibleMismatch = docIds.some((id) => {
                const d = docs.find((x) => x.id === id);
                return d?.portal_visible && targetFolder && !targetFolder.portal_visible;
            });
            const count = docIds.length;
            if (anyVisibleMismatch) {
                toast.warning(`Moved ${count} document(s) — but this folder is hidden from the portal, so the client won't see ${count > 1 ? "them" : "it"} here.`);
            } else {
                toast.success(`Moved ${count} document${count > 1 ? "s" : ""} to ${targetFolderId ? "folder" : "Unfiled"}`);
            }
            setSelectedDocIds([]);
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Move failed");
        }
    }, [invalidate, docs, folderById]);

    const onReparent = useCallback(async (folderId, newParentId) => {
        try {
            await api.patch(`/folders/${folderId}`, { parent_id: newParentId });
            toast.success("Folder moved");
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not move folder");
            invalidate(); // rollback to server truth
        }
    }, [invalidate]);

    // ---- folder CRUD ----
    async function createFolder() {
        const name = draftName.trim();
        if (!name) return;
        try {
            await api.post("/folders", { client_id: clientId, project_id: projectId, scope: "project", name, parent_id: newFolder?.parent_id || null });
            toast.success("Folder created");
            setNewFolder(null); setDraftName(""); invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
        }
    }
    async function saveRename() {
        const name = draftName.trim();
        if (!name) return;
        try {
            await api.patch(`/folders/${renameTarget.id}`, { name });
            toast.success("Folder renamed");
            setRenameTarget(null); setDraftName(""); invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Rename failed");
        }
    }
    async function deleteFolder(f) {
        if (!window.confirm(`Delete folder "${f.name}"? It must be empty.`)) return;
        try {
            await api.delete(`/folders/${f.id}`);
            toast.success("Folder deleted");
            if (selectedId === f.id) setSelectedId("__all__");
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    }
    async function toggleVisibility(f) {
        try {
            await api.patch(`/folders/${f.id}`, { portal_visible: !f.portal_visible });
            invalidate();
        } catch (e) {
            toast.error("Could not update visibility");
        }
    }
    async function publishFolder(f) {
        if (!window.confirm(`Publish "${f.name}" (and any sub-folders + documents inside it) to the client portal?`)) return;
        try {
            const { data } = await api.post(`/folders/${f.id}/publish`, { include_documents: true });
            toast.success(`Published — the client can now see this folder${data ? " and its contents" : ""}.`);
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Publish failed");
        }
    }
    async function bulkSetVisibility(portalVisible) {
        if (!selectedDocIds.length) return;
        try {
            await api.post("/documents/bulk-visibility", { document_ids: selectedDocIds, portal_visible: portalVisible });
            toast.success(portalVisible ? `Published ${selectedDocIds.length} document(s) to the portal` : `Hid ${selectedDocIds.length} document(s) from the portal`);
            setSelectedDocIds([]);
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Bulk update failed");
        }
    }
    async function downloadFolder(f) {
        try {
            const resp = await api.get(`/folders/${f.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
            const a = document.createElement("a");
            a.href = url; a.download = `${f.name}.zip`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    }
    async function downloadDoc(doc) {
        try {
            const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] || doc.mime_type }));
            const a = document.createElement("a");
            a.href = url; a.download = doc.filename || `document-${doc.id.slice(0, 8)}`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Download failed");
        }
    }

    async function fileReport(doc, folderId, createRule) {
        try {
            await api.patch(`/documents/${doc.id}`, { folder_id: folderId, create_filing_rule: !!createRule });
            toast.success(createRule ? "Filed — future reports of this type will auto-file here" : "Filed");
            invalidate();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not file");
        }
    }

    function openUpload() {
        const folderId = (selectedId !== "__all__" && selectedId !== "__unfiled__") ? selectedId : null;
        setUploadPrefill({
            client_id: clientId, project_id: projectId, project_label: projectLabel,
            folder_id: folderId, folder_label: folderId ? folderById[folderId]?.name : null,
        });
        setUploadOpen(true);
    }
    function openBulk() {
        const folderId = (selectedId !== "__all__" && selectedId !== "__unfiled__") ? selectedId : null;
        setBulkPrefill({
            client_id: clientId, project_id: projectId, project_label: projectLabel,
            folder_id: folderId, folder_label: folderId ? folderById[folderId]?.name : null,
        });
        setBulkOpen(true);
    }

    if (!enabled) {
        return <div data-testid="document-browser-empty" className="text-[13px] text-[#9A968F] py-10 text-center">Select a client and project to browse folders.</div>;
    }

    const selectedFolder = folderById[selectedId];
    const loading = foldersQ.isLoading || docsQ.isLoading;

    return (
        <div data-testid="document-browser" className="space-y-3">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-lg border border-[#E5E0DA] overflow-hidden">
                    <button data-testid="docbrowser-view-folder" onClick={() => setView("folder")}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold ${view === "folder" ? "bg-[#FBEFE5] text-[#9F4212]" : "bg-white text-[#6F6A64]"}`}>
                        <FolderTreeIcon size={14} /> Folders
                    </button>
                    <button data-testid="docbrowser-view-flat" onClick={() => setView("flat")}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold border-l border-[#E5E0DA] ${view === "flat" ? "bg-[#FBEFE5] text-[#9F4212]" : "bg-white text-[#6F6A64]"}`}>
                        <List size={14} /> Flat view
                    </button>
                </div>
                <div className="ml-auto flex items-center gap-2">
                    {canCreateReport && (
                        <CreateReportMenu clientId={clientId} projectId={projectId} projectLabel={projectLabel}
                            backTo={`/app/projects/${projectId}`}
                            allocations={generatableAllocs} onGenerate={generateReport} />
                    )}
                    {canManageFolders && view === "folder" && (
                        <button data-testid="docbrowser-new-folder" onClick={() => { setNewFolder({ parent_id: (selectedId !== "__all__" && selectedId !== "__unfiled__") ? selectedId : null }); setDraftName(""); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                            <FolderPlus size={14} /> New folder
                        </button>
                    )}
                    {canEdit && (
                        <button data-testid="docbrowser-bulk-upload" onClick={openBulk}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                            <Files size={14} /> Bulk upload
                        </button>
                    )}
                    {canEdit && (
                        <button data-testid="docbrowser-upload" onClick={openUpload}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            <Upload size={14} /> Upload
                        </button>
                    )}
                </div>
            </div>

            {selectedDocIds.length > 0 && isInternal && (
                <div data-testid="docbrowser-bulk-bar" className="flex items-center gap-3 px-4 py-2 rounded-lg" style={{ background: "#FBEFE5" }}>
                    <span className="text-[12.5px] font-semibold" style={{ color: "#9F4212" }}>{selectedDocIds.length} selected</span>
                    {view === "folder" && (
                        <span className="text-[11.5px] italic" style={{ color: "#9F4212" }}>Drag any of them to a folder to move all {selectedDocIds.length}</span>
                    )}
                    <button data-testid="docbrowser-bulk-publish" onClick={() => bulkSetVisibility(true)}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: "#2E6B57" }}>
                        <Megaphone size={13} /> Publish to portal
                    </button>
                    <button data-testid="docbrowser-bulk-hide" onClick={() => bulkSetVisibility(false)}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border bg-white" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                        <EyeOff size={13} /> Hide from portal
                    </button>
                    <button data-testid="docbrowser-bulk-clear" onClick={() => setSelectedDocIds([])}
                        className="text-[12px] font-semibold underline" style={{ color: "#9F4212" }}>Clear</button>
                </div>
            )}

            {view === "flat" ? (
                <DocsTable docs={docs} loading={loading} clientMap={clientMap} buildingMap={buildingMap}
                    onOpen={setDetailDoc} onDownload={downloadDoc} onPreview={setPreviewDoc} draggable={false}
                    selectable={isInternal} selectedIds={selectedDocIds} onSelectionChange={setSelectedDocIds} showPortalInfo={showPortalInfo}
                    onOpenReport={(id) => navigate(`/app/inspections/${id}?back=${encodeURIComponent(`/app/projects/${projectId}?tab=documents`)}`)}
                    folders={folders} canFile={canManageFolders} canCreateRule={canCreateFilingRule} onFileReport={fileReport} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
                    {/* Left rail */}
                    <div className="rounded-xl bg-white border border-[#E5E0DA] p-2.5 self-start" data-testid="docbrowser-rail">
                        {loading ? (
                            <div className="space-y-2 p-1">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}</div>
                        ) : (
                            <FolderTree folders={folders} docCounts={docCounts} totalCount={docs.length} unfiledCount={unfiledCount}
                                selectedId={selectedId} onSelect={setSelectedId} onDropDoc={onDropDoc} onReparent={onReparent}
                                canEdit={canManageFolders} isInternal={isInternal} />
                        )}
                    </div>

                    {/* Main panel */}
                    <div className="rounded-xl bg-white border border-[#E5E0DA] overflow-hidden min-w-0">
                        {/* Breadcrumb + folder actions */}
                        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#F0EDE8] flex-wrap" data-testid="docbrowser-breadcrumb">
                            <button onClick={() => setSelectedId("__all__")} className="inline-flex items-center gap-1 text-[12.5px] text-[#6F6A64] hover:text-[#1F1F1F]">
                                <Home size={13} /> {projectLabel || "Project"}
                            </button>
                            {breadcrumb.map((f) => (
                                <span key={f.id} className="inline-flex items-center gap-2">
                                    <ChevronRight size={12} className="text-[#C8C2B8]" />
                                    <button onClick={() => setSelectedId(f.id)} className={`text-[12.5px] ${f.id === selectedId ? "font-semibold text-[#1F1F1F]" : "text-[#6F6A64] hover:text-[#1F1F1F]"}`}>{f.name}</button>
                                </span>
                            ))}
                            {selectedId === "__unfiled__" && <span className="text-[12.5px] font-semibold text-[#1F1F1F]"><ChevronRight size={12} className="inline text-[#C8C2B8]" /> Unfiled</span>}

                            {selectedFolder && canManageFolders && (
                                <span className="ml-auto flex items-center gap-1">
                                    {isInternal && (
                                        <button data-testid={`folder-issue-${selectedFolder.id}`} onClick={() => setIssueFor(selectedFolder)}
                                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-white font-semibold mr-1" style={{ background: "var(--brand-accent,#C75A1B)" }} title="Issue / allocate into this folder">
                                            <Send size={12} /> Issue into folder
                                        </button>
                                    )}
                                    {isInternal && (
                                        <button data-testid={`folder-publish-${selectedFolder.id}`} onClick={() => publishFolder(selectedFolder)}
                                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md text-white font-semibold mr-1" style={{ background: "#2E6B57" }} title="Publish this folder (+ sub-folders + documents) to the portal">
                                            <Megaphone size={12} /> Publish this folder to the portal
                                        </button>
                                    )}
                                    {isInternal && (
                                        <button data-testid={`folder-visibility-${selectedFolder.id}`} onClick={() => toggleVisibility(selectedFolder)}
                                            className="text-[11px] px-2 py-1 rounded-md hover:bg-[#F6F4F0] text-[#6F6A64]">
                                            {selectedFolder.portal_visible ? "Hide from portal" : "Show in portal"}
                                        </button>
                                    )}
                                    <button data-testid={`folder-rename-${selectedFolder.id}`} onClick={() => { setRenameTarget(selectedFolder); setDraftName(selectedFolder.name); }}
                                        className="p-1.5 rounded-md hover:bg-[#F6F4F0] text-[#6F6A64]" title="Rename"><Pencil size={13} /></button>
                                    <button data-testid={`folder-download-${selectedFolder.id}`} onClick={() => downloadFolder(selectedFolder)}
                                        className="p-1.5 rounded-md hover:bg-[#F6F4F0] text-[#6F6A64]" title="Download .zip"><Download size={13} /></button>
                                    {!selectedFolder.system_locked && (
                                        <button data-testid={`folder-delete-${selectedFolder.id}`} onClick={() => deleteFolder(selectedFolder)}
                                            className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Delete"><Trash2 size={13} /></button>
                                    )}
                                </span>
                            )}
                        </div>
                        <TemplatesReportsSection
                            allocations={selectedId === "__all__" ? allocations : allocations.filter((a) => a.folder_id === selectedId)}
                            reports={selectedId === "__all__" ? genReports : selectedId === "__unfiled__" ? genReports.filter((r) => !r.folder_id) : genReports.filter((r) => r.folder_id === selectedId)}
                            canGenerateAlloc={canGenerateAlloc} isInternal={isInternal}
                            onGenerate={generateReport} onDelete={deleteAllocation}
                            onOpenReport={(r) => navigate(`/app/inspections/${r.id}?back=${encodeURIComponent(`/app/projects/${projectId}`)}`)} />
                        <DocsTable docs={visibleDocs} loading={loading} clientMap={clientMap} buildingMap={buildingMap}
                            onOpen={setDetailDoc} onDownload={downloadDoc} onPreview={setPreviewDoc} draggable={canManageFolders}
                            selectable={isInternal} selectedIds={selectedDocIds} onSelectionChange={setSelectedDocIds} showPortalInfo={showPortalInfo}
                            onOpenReport={(id) => navigate(`/app/inspections/${id}?back=${encodeURIComponent(`/app/projects/${projectId}?tab=documents`)}`)}
                            folders={folders} canFile={canManageFolders} canCreateRule={canCreateFilingRule} onFileReport={fileReport}
                            emptyHint={selectedId === "__all__" ? "No documents in this project yet." : "This folder is empty. Drag documents here or upload."} />
                    </div>
                </div>
            )}

            {/* New folder modal */}
            {newFolder && (
                <FolderNameModal title="New folder" value={draftName} onChange={setDraftName}
                    onCancel={() => { setNewFolder(null); setDraftName(""); }} onSave={createFolder}
                    hint={newFolder.parent_id ? `Inside: ${folderById[newFolder.parent_id]?.name}` : "Top-level folder"} testid="new-folder-modal" />
            )}
            {renameTarget && (
                <FolderNameModal title="Rename folder" value={draftName} onChange={setDraftName}
                    onCancel={() => { setRenameTarget(null); setDraftName(""); }} onSave={saveRename} testid="rename-folder-modal" />
            )}

            {uploadOpen && (
                <UploadDialog clients={clients} buildings={buildingsQ.data || []} meta={metaQ.data || { categories: [], statuses: [] }}
                    prefill={uploadPrefill} portalClientId={!isInternal ? clientId : null} folders={folders}
                    onClose={() => { setUploadOpen(false); setUploadPrefill(null); }}
                    onCreated={() => { setUploadOpen(false); setUploadPrefill(null); invalidate(); }} />
            )}
            {bulkOpen && (
                <BulkUploadDialog clients={clients} buildings={buildingsQ.data || []} meta={metaQ.data || { categories: [], statuses: [] }}
                    prefill={bulkPrefill} portalClientId={!isInternal ? clientId : null} folders={folders}
                    onClose={() => { setBulkOpen(false); setBulkPrefill(null); }}
                    onUploaded={invalidate} />
            )}
            {previewDoc && (
                <DocumentPreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} onDownload={downloadDoc} />
            )}
            {detailDoc && (
                <DocumentDetail doc={detailDoc} clientMap={clientMap} buildingMap={buildingMap} canEdit={canEdit} canApprove={canApprove}
                    onClose={() => setDetailDoc(null)} onChanged={(d) => { setDetailDoc(d); invalidate(); }}
                    onDeleted={() => { setDetailDoc(null); invalidate(); }} onDownload={downloadDoc} onPreview={setPreviewDoc} />
            )}
            {issueFor && (
                <PublishWizard
                    user={user} clients={clients}
                    preset={{ clientId, projectId, folderId: issueFor.id, folderName: issueFor.name, projectLabel }}
                    onClose={() => setIssueFor(null)}
                    onIssued={() => invalidate()} />
            )}
        </div>
    );
}

function DocsTable({ docs, loading, clientMap, buildingMap, onOpen, onDownload, onPreview, draggable, emptyHint, selectable, selectedIds = [], onSelectionChange, showPortalInfo = false, onOpenReport, folders = [], canFile = false, canCreateRule = false, onFileReport }) {
    const [filingDoc, setFilingDoc] = useState(null);
    const todayIso = new Date().toISOString().slice(0, 10);
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(docs, {
        accessors: {
            title: (r) => r.title || "",
            category: (r) => r.category || "",
            owner: (r) => r.owner_name || "",
            expiry_date: (r) => r.expiry_date || "",
            created_at: (r) => r.created_at || "",
            updated_at: (r) => r.updated_at || r.created_at || "",
            version: (r) => r.version_number || 0,
            status: (r) => r.status || "",
        },
    });
    const allSelected = sorted.length > 0 && sorted.every((r) => selectedIds.includes(r.id));
    const toggleAll = () => onSelectionChange(allSelected ? [] : sorted.map((r) => r.id));
    const toggleOne = (id) => onSelectionChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
    const colCount = 8 + (selectable ? 1 : 0) + (showPortalInfo ? 1 : 0);
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                    <tr>
                        {selectable && (
                            <th className="px-3 py-2.5 w-8">
                                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="docs-select-all" />
                            </th>
                        )}
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="title" onClick={toggleSort} testId="docs-th-title">Document</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="category" onClick={toggleSort} testId="docs-th-category">Category</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="owner" onClick={toggleSort} testId="docs-th-owner">Owner</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="expiry_date" onClick={toggleSort} testId="docs-th-expiry">Review · Expiry</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="created_at" onClick={toggleSort} testId="docs-th-created">Date issued</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="updated_at" onClick={toggleSort} testId="docs-th-updated">Date modified</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="version" onClick={toggleSort} testId="docs-th-version">v</SortableTh>
                        <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="status" onClick={toggleSort} testId="docs-th-status">Status</SortableTh>
                        {showPortalInfo && <th className="px-3 py-2.5 text-left">Portal</th>}
                        <th className="px-3 py-2.5 text-right"></th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        Array.from({ length: 4 }).map((_, i) => (
                            <tr key={i} className="border-b border-[#F0EDE8]"><td colSpan={colCount} className="px-4 py-2.5"><Skeleton className="h-6 w-full" /></td></tr>
                        ))
                    ) : sorted.length === 0 ? (
                        <tr><td colSpan={colCount} className="text-center py-10 text-[#9A968F] text-[13px]" data-testid="docbrowser-empty">{emptyHint || "No documents."}</td></tr>
                    ) : sorted.map((r) => {
                        const expired = r.expiry_date && r.expiry_date <= todayIso;
                        return (
                            <tr key={r.id} data-testid={`document-row-${r.id}`}
                                draggable={draggable}
                                onDragStart={draggable ? (e) => {
                                    const ids = (selectable && selectedIds.includes(r.id) && selectedIds.length > 1) ? selectedIds : [r.id];
                                    e.dataTransfer.setData("pr/docs", JSON.stringify(ids));
                                } : undefined}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={() => onOpen(r)}>
                                {selectable && (
                                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleOne(r.id)} data-testid={`document-select-${r.id}`} />
                                    </td>
                                )}
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                        <FileText size={14} className="text-[#9A968F] shrink-0" />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[#1F1F1F] truncate">{r.title}</div>
                                            <div className="text-[11px] text-[#9A968F] truncate">{r.filename || "—"} · {formatBytes(r.size)}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 text-[#6F6A64] text-[12px]">{r.category}</td>
                                <td className="px-3 py-2.5 text-[#6F6A64] text-[12px]">{r.owner_name || "—"}</td>
                                <td className="px-3 py-2.5 text-[12px]">
                                    <div className="text-[#6F6A64]">{r.review_date || "—"}</div>
                                    <div className={expired ? "text-red-600 font-semibold" : "text-[#9A968F]"}>{r.expiry_date || "—"}</div>
                                </td>
                                <td className="px-3 py-2.5 text-[12px] text-[#6F6A64] whitespace-nowrap">{fmtDate(r.created_at)}</td>
                                <td className="px-3 py-2.5 text-[12px] text-[#6F6A64] whitespace-nowrap">{fmtDate(r.updated_at || r.created_at)}</td>
                                <td className="px-3 py-2.5 font-mono text-[12px] text-[#6F6A64]">v{r.version_number}</td>
                                <td className="px-3 py-2.5"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                {showPortalInfo && (
                                    <td className="px-3 py-2.5" data-testid={`document-portal-badge-${r.id}`}>
                                        {r.portal_visible ? (
                                            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#E4F1EC", color: "#2E6B57" }}>
                                                <Megaphone size={10} /> Visible to client
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>
                                                <EyeOff size={10} /> Hidden
                                            </span>
                                        )}
                                    </td>
                                )}
                                <td className="px-2 py-2.5 text-right whitespace-nowrap">
                                    <div className="inline-flex items-center gap-0.5">
                                        {r.source_form_instance_id && (
                                            <button onClick={(e) => { e.stopPropagation(); onOpenReport && onOpenReport(r.source_form_instance_id); }} data-testid={`document-open-report-${r.id}`}
                                                className="p-1.5 rounded-md hover:bg-[#FBEFE5]" style={{ color: "#9F4212" }} title="Open the live report"><Send size={13} className="rotate-45" /></button>
                                        )}
                                        {canFile && r.source_form_instance_id && !r.folder_id && (
                                            <button onClick={(e) => { e.stopPropagation(); setFilingDoc(r); }} data-testid={`document-file-${r.id}`}
                                                className="p-1.5 rounded-md hover:bg-[#F6F4F0] text-[#6F6A64]" title="File into a folder"><FolderPlus size={13} /></button>
                                        )}
                                        <button onClick={(e) => { e.stopPropagation(); onPreview && onPreview(r); }} data-testid={`document-preview-${r.id}`}
                                            className="p-1.5 rounded-md hover:bg-[#F6F4F0] text-[#6F6A64]" title="Preview"><Eye size={13} /></button>
                                        <button onClick={(e) => { e.stopPropagation(); onDownload(r); }} data-testid={`document-download-${r.id}`}
                                            className="p-1.5 rounded-md hover:bg-[#F6F4F0]" style={{ color: "#2A5DB0" }} title="Download"><Download size={13} /></button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {filingDoc && (
                <FileReportModal doc={filingDoc} folders={folders} canCreateRule={canCreateRule}
                    onCancel={() => setFilingDoc(null)}
                    onConfirm={(folderId, createRule) => { onFileReport(filingDoc, folderId, createRule); setFilingDoc(null); }} />
            )}
        </div>
    );
}

function FileReportModal({ doc, folders, canCreateRule, onCancel, onConfirm }) {
    const [folderId, setFolderId] = useState("");
    const [createRule, setCreateRule] = useState(canCreateRule);
    const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);
    const folderLabel = (f) => {
        const parent = f.parent_id ? folderById[f.parent_id] : null;
        return parent ? `${parent.name} / ${f.name}` : f.name;
    };
    return (
        <div className="fixed inset-0 z-[55] bg-black/40 grid place-items-center p-4" data-testid="file-report-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-bold text-[#1F1F1F]">File this report</h4>
                    <button onClick={onCancel} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <p className="text-[12px] text-[#9A968F] mb-3 truncate">{doc.title}</p>
                <select data-testid="file-report-folder-select" value={folderId} onChange={(e) => setFolderId(e.target.value)}
                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm mb-3">
                    <option value="">— Select a folder —</option>
                    {folders.map((f) => <option key={f.id} value={f.id}>{folderLabel(f)}</option>)}
                </select>
                {canCreateRule && (
                    <label className="flex items-center gap-2 text-[12.5px] text-[#6F6A64] mb-4">
                        <input type="checkbox" data-testid="file-report-create-rule" checked={createRule} onChange={(e) => setCreateRule(e.target.checked)} />
                        Always file future reports of this type here
                    </label>
                )}
                <div className="flex justify-end gap-2">
                    <button onClick={onCancel} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="file-report-save" onClick={() => onConfirm(folderId, createRule)} disabled={!folderId}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Check size={14} /> File
                    </button>
                </div>

            </div>
        </div>
    );
}

function TemplatesReportsSection({ allocations = [], reports = [], canGenerateAlloc, isInternal, onGenerate, onDelete, onOpenReport }) {
    if (allocations.length === 0 && reports.length === 0) return null;
    const REPORT_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "neutral" };
    return (
        <div data-testid="templates-reports-section" className="px-4 py-3 border-b" style={{ borderColor: "#F0EDE8", background: "#FCFBF9" }}>
            {allocations.length > 0 && (
                <div className="space-y-2">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F]">Report templates</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {allocations.map((a) => (
                            <div key={a.id} data-testid={`allocation-card-${a.id}`}
                                className="flex items-center gap-3 p-2.5 rounded-xl border bg-white" style={{ borderColor: "#E5E0DA" }}>
                                <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: "#EAF3EF" }}>
                                    <FilePlus2 size={15} className="text-[#2E6B57]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-semibold text-[13px] text-[#1F1F1F] truncate">{a.title || a.template_name}</span>
                                        <span className="text-[9.5px] uppercase font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#E4F1EC", color: "#2E6B57" }}>Template</span>
                                    </div>
                                    <div className="text-[11px] text-[#9A968F] truncate">
                                        {a.folder_name} · Completed by {a.completed_by === "client" ? "Client" : "PR"}{a.scope === "all_projects" ? " · All projects" : ""}
                                    </div>
                                </div>
                                {canGenerateAlloc(a) ? (
                                    <button data-testid={`allocation-generate-${a.id}`} onClick={() => onGenerate(a)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-semibold text-white shrink-0" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                        <FilePlus2 size={12} /> Create a report
                                    </button>
                                ) : (
                                    <span className="text-[10.5px] text-[#9A968F] italic shrink-0">PR completes on visit</span>
                                )}
                                {isInternal && (
                                    <button data-testid={`allocation-delete-${a.id}`} onClick={() => onDelete(a)}
                                        className="p-1.5 rounded-md hover:bg-red-50 text-red-500 shrink-0" title="Remove template"><Trash2 size={13} /></button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {reports.length > 0 && (
                <div className="space-y-1.5 mt-3">
                    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F]">Generated reports</div>
                    <div className="rounded-xl border bg-white divide-y" style={{ borderColor: "#E5E0DA" }}>
                        {reports.map((r) => (
                            <button key={r.id} data-testid={`generated-report-${r.id}`} onClick={() => onOpenReport(r)}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#F6F4F0]">
                                <FileText size={14} className="text-[#9A968F] shrink-0" />
                                <span className="min-w-0 flex-1">
                                    <span className="block font-semibold text-[12.5px] text-[#1F1F1F] truncate">{r.title || r.template_name}</span>
                                    <span className="block text-[11px] text-[#9A968F] truncate">{fmtDate(r.updated_at || r.created_at)}</span>
                                </span>
                                <Pill tone={REPORT_TONE[r.status] || "neutral"}>{r.status}</Pill>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function FolderNameModal({ title, value, onChange, onCancel, onSave, hint, testid }) {
    return (
        <div className="fixed inset-0 z-[55] bg-black/40 grid place-items-center p-4" data-testid={testid}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-bold text-[#1F1F1F]">{title}</h4>
                    <button onClick={onCancel} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                {hint && <p className="text-[12px] text-[#9A968F] mb-2">{hint}</p>}
                <input autoFocus value={value} onChange={(e) => onChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") onSave(); }}
                    data-testid="folder-name-input" placeholder="Folder name"
                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]" />
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onCancel} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="folder-name-save" onClick={onSave} disabled={!value.trim()}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Check size={14} /> Save
                    </button>
                </div>
            </div>
        </div>
    );
}
